import { useSuspenseQuery } from "@tanstack/react-query"
import { createFileRoute } from "@tanstack/react-router"
import { FileCheck, FileText } from "lucide-react"

import { logger } from "@repo/utils/logger"
import { getResourceDocumentsQueryOptions } from "~/modules/resources/resource-documents.queries"
import { getExtendedResourceBySlugQueryOptions } from "~/modules/resources/resources.queries"
import { DocumentLink } from "~/modules/storage/components/document-link"

const log = logger.child({
  module: "resources-resource-documents-route",
})

export const Route = createFileRoute(
  "/_protected-layout/_os-layout/resources/$resourceSlug/documents",
)({
  component: RouteComponent,
  loader: async ({ context, params }) => {
    context.queryClient
      .ensureQueryData(
        getExtendedResourceBySlugQueryOptions({ slug: params.resourceSlug }),
      )
      .catch((error) => {
        log.error("Error getting extended resource by slug", { error })
        return null
      })
  },
})

function RouteComponent() {
  const params = Route.useParams()

  const { data: resource } = useSuspenseQuery(
    getExtendedResourceBySlugQueryOptions({ slug: params.resourceSlug }),
  )

  const { data: documents } = useSuspenseQuery(
    getResourceDocumentsQueryOptions({ resourceId: resource?.id || "" }),
  )

  if (!resource) {
    return <div>Resource not found</div>
  }

  if (!documents || documents.length === 0) {
    return (
      <div className="flex h-32 items-center justify-center">
        <p className="text-sm text-white/70">No documents found</p>
      </div>
    )
  }

  return (
    <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
      {documents.map((doc) => (
        <DocumentLink
          key={doc.id}
          document={doc}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-3 rounded-lg bg-white/5 p-3 transition-colors hover:bg-white/10"
        >
          <FileText className="h-6 w-6 text-white/70" />
          <div className="flex-grow">
            <p className="font-medium">
              {(doc.metadata as { original_filename?: string })
                ?.original_filename || "Document"}
            </p>
            <p className="text-white/70 text-xs capitalize">{doc.doc_type}</p>
          </div>
          <FileCheck className="h-5 w-5 text-green-500" />
        </DocumentLink>
      ))}
    </div>
  )
}
